import { apiSlice } from '../../../store/api/apiSlice';
import type {
  AdminAppointment,
  AdminAppointmentMutationResult,
  AdminAppointmentPage,
  AdminAppointmentSchedulingContext,
  AdminAppointmentSchedulingContextRequest,
  AdminMutationReview,
  AdoptAppointmentCommand,
  AppointmentHistoryPage,
  AppointmentOperationStatus,
  BookAdminAppointmentCommand,
  CancelAdminAppointmentCommand,
  RebindAppointmentCommand,
  RescheduleAdminAppointmentCommand,
} from '../../appointments/types';

interface PageRequest {
  limit?: number;
  cursor?: string | null;
}

interface ReviewQueueRequest extends PageRequest {
  reason?: 'ambiguous_provider_rewrite' | 'ambiguous_provider_deletion_scope' | null;
}

export const adminAppointmentsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAdminAppointments: builder.query<AdminAppointmentPage, PageRequest | undefined>({
      query: (request) => ({
        url: '/admin/appointments',
        params: cleanParams(request),
      }),
      providesTags: ['Appointments'],
    }),
    getAdminAppointment: builder.query<AdminAppointment, string>({
      query: (appointmentId) => `/admin/appointments/${encodeURIComponent(appointmentId)}`,
      providesTags: (_result, _error, appointmentId) => [
        { type: 'Appointments', id: appointmentId },
      ],
    }),
    getAdminAppointmentHistory: builder.query<AppointmentHistoryPage, string>({
      query: (appointmentId) => `/admin/appointments/${encodeURIComponent(appointmentId)}/history`,
      providesTags: (_result, _error, appointmentId) => [
        { type: 'Appointments', id: `${appointmentId}:history` },
      ],
    }),
    getAdminAppointmentOperation: builder.query<AppointmentOperationStatus, string>({
      query: (operationId) => `/admin/appointment-operations/${encodeURIComponent(operationId)}`,
      providesTags: (_result, _error, operationId) => [
        { type: 'Appointments', id: `operation:${operationId}` },
      ],
    }),
    getAdminAppointmentSchedulingContext: builder.query<
      AdminAppointmentSchedulingContext,
      AdminAppointmentSchedulingContextRequest
    >({
      query: (request) => ({
        url: '/admin/appointments/scheduling-context',
        params: cleanParams(request),
      }),
      providesTags: ['Appointments', 'CalendarSettings'],
    }),
    getAdminAppointmentReviewQueue: builder.query<
      AdminAppointmentPage,
      ReviewQueueRequest | undefined
    >({
      query: (request) => ({
        url: '/admin/appointments/review',
        params: cleanParams(request),
      }),
      providesTags: ['Appointments'],
    }),
    adoptAdminAppointment: builder.mutation<
      AdminMutationReview | AdminAppointmentMutationResult,
      AdoptAppointmentCommand
    >({
      query: (command) => ({
        url: '/admin/appointments/adopt',
        method: 'POST',
        body: command,
      }),
      invalidatesTags: ['Appointments'],
    }),
    rebindAdminAppointment: builder.mutation<
      AdminMutationReview | AdminAppointmentMutationResult,
      { appointmentId: string; command: RebindAppointmentCommand }
    >({
      query: ({ appointmentId, command }) => ({
        url: `/admin/appointments/${encodeURIComponent(appointmentId)}/rebind`,
        method: 'POST',
        body: command,
      }),
      invalidatesTags: (_result, _error, { appointmentId }) => [
        'Appointments',
        { type: 'Appointments', id: appointmentId },
      ],
    }),
    bookAdminAppointment: builder.mutation<
      AdminAppointmentMutationResult,
      BookAdminAppointmentCommand
    >({
      query: (command) => ({
        url: '/admin/appointments',
        method: 'POST',
        body: command,
      }),
      invalidatesTags: (result, error) =>
        terminalMutationResult(result, error) ? ['Appointments'] : [],
    }),
    rescheduleAdminAppointment: builder.mutation<
      AdminAppointmentMutationResult,
      { appointmentId: string; command: RescheduleAdminAppointmentCommand }
    >({
      query: ({ appointmentId, command }) => ({
        url: `/admin/appointments/${encodeURIComponent(appointmentId)}/reschedule`,
        method: 'POST',
        body: command,
      }),
      invalidatesTags: (result, error, { appointmentId }) =>
        terminalMutationResult(result, error)
          ? [
              'Appointments',
              { type: 'Appointments', id: appointmentId },
              { type: 'Appointments', id: `${appointmentId}:history` },
            ]
          : [],
    }),
    cancelAdminAppointment: builder.mutation<
      AdminAppointmentMutationResult,
      { appointmentId: string; command: CancelAdminAppointmentCommand }
    >({
      query: ({ appointmentId, command }) => ({
        url: `/admin/appointments/${encodeURIComponent(appointmentId)}/cancel`,
        method: 'POST',
        body: command,
      }),
      invalidatesTags: (result, error, { appointmentId }) =>
        terminalMutationResult(result, error)
          ? [
              'Appointments',
              { type: 'Appointments', id: appointmentId },
              { type: 'Appointments', id: `${appointmentId}:history` },
            ]
          : [],
    }),
  }),
});

function cleanParams(params: object | undefined) {
  return Object.fromEntries(
    Object.entries(params ?? {}).filter(([, value]) => value !== undefined && value !== null)
  );
}

function terminalMutationResult(result: unknown, error: unknown): boolean {
  const response = result ?? structuredErrorData(error);
  if (typeof response !== 'object' || response === null || !('status' in response)) return false;
  return response.status === 'confirmed' || response.status === 'rejected';
}

function structuredErrorData(error: unknown): unknown {
  if (typeof error !== 'object' || error === null || !('data' in error)) return undefined;
  return error.data;
}

export const {
  useGetAdminAppointmentsQuery,
  useGetAdminAppointmentQuery,
  useLazyGetAdminAppointmentQuery,
  useGetAdminAppointmentHistoryQuery,
  useGetAdminAppointmentOperationQuery,
  useLazyGetAdminAppointmentOperationQuery,
  useGetAdminAppointmentSchedulingContextQuery,
  useLazyGetAdminAppointmentSchedulingContextQuery,
  useGetAdminAppointmentReviewQueueQuery,
  useAdoptAdminAppointmentMutation,
  useRebindAdminAppointmentMutation,
  useBookAdminAppointmentMutation,
  useRescheduleAdminAppointmentMutation,
  useCancelAdminAppointmentMutation,
} = adminAppointmentsApi;
